/**
 * Pre-upload image pipeline: validate type, convert HEIC/HEIF to JPEG,
 * downscale + compress, and return a small JPEG suitable for upload over
 * slow mobile networks.
 *
 * The cloud function in functions/image.js derives 200px and 800px versions
 * from the upload, so 1600px is more than enough source detail. Capping at
 * ~1MB keeps airport-network uploads in the "seconds" range.
 */

import imageCompression from 'browser-image-compression'
import AppSettings from '@/app-settings.json'

export const PrepareErrorCode = {
  UNSUPPORTED_TYPE: 'unsupported_type',
  HEIC_CONVERSION_FAILED: 'heic_conversion_failed',
  COMPRESSION_FAILED: 'compression_failed',
  TOO_LARGE: 'too_large'
}

export class PrepareError extends Error {
  constructor (code, cause) {
    super(code)
    this.name = 'PrepareError'
    this.code = code
    this.cause = cause
  }
}

const ALLOWED_EXTENSIONS = ['heic', 'heif', 'jpg', 'jpeg', 'png', 'webp', 'gif', 'avif']
const HEIC_EXTENSIONS = ['heic', 'heif']
const HEIC_MIME_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']
const HARD_SIZE_CAP_BYTES = 8 * 1024 * 1024

const COMPRESS_OPTIONS = AppSettings.imageUpload.compressOptions

function getExtension (name) {
  if (!name) return ''
  const idx = name.lastIndexOf('.')
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : ''
}

function isHeic (file) {
  if (HEIC_MIME_TYPES.includes((file.type || '').toLowerCase())) return true
  return HEIC_EXTENSIONS.includes(getExtension(file.name))
}

function isAcceptedType (file) {
  const ext = getExtension(file.name)
  const mime = (file.type || '').toLowerCase()
  if (mime.startsWith('image/')) return true
  // Android sometimes hands us an empty `type` for HEIC and other newer
  // formats — accept based on extension as a fallback.
  return ALLOWED_EXTENSIONS.includes(ext)
}

function renameToJpeg (originalName) {
  if (!originalName) return 'image.jpg'
  const idx = originalName.lastIndexOf('.')
  const base = idx >= 0 ? originalName.slice(0, idx) : originalName
  return `${base}.jpg`
}

async function convertHeicToJpeg (file) {
  // Dynamic import keeps heic2any (and its WASM payload) out of the main
  // bundle for users who never upload HEIC.
  const heic2any = (await import('heic2any')).default
  let converted
  try {
    converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 })
  } catch (err) {
    throw new PrepareError(PrepareErrorCode.HEIC_CONVERSION_FAILED, err)
  }
  // heic2any may return an array (one per HEIC frame) — take the first.
  const blob = Array.isArray(converted) ? converted[0] : converted
  return new File([blob], renameToJpeg(file.name), { type: 'image/jpeg' })
}

/**
 * Prepare a user-selected image file for upload.
 * @param {File} file - The file from <input type="file">.
 * @returns {Promise<{file: File, originalName: string, originalSize: number, finalSize: number, wasConverted: boolean}>}
 * @throws {PrepareError}
 */
export async function prepareImageForUpload (file) {
  if (!file) throw new PrepareError(PrepareErrorCode.UNSUPPORTED_TYPE)
  if (!isAcceptedType(file)) throw new PrepareError(PrepareErrorCode.UNSUPPORTED_TYPE)

  const originalName = file.name
  const originalSize = file.size
  let working = file
  let wasConverted = false

  if (isHeic(file)) {
    working = await convertHeicToJpeg(file)
    wasConverted = true
  }

  let compressed
  try {
    compressed = await imageCompression(working, COMPRESS_OPTIONS)
  } catch (err) {
    throw new PrepareError(PrepareErrorCode.COMPRESSION_FAILED, err)
  }

  if (compressed.size > HARD_SIZE_CAP_BYTES) {
    throw new PrepareError(PrepareErrorCode.TOO_LARGE)
  }

  // Ensure the final File has a .jpg name so Storage's contentType inference
  // and our string-replace thumbUrl logic both work cleanly.
  const finalFile = compressed instanceof File
    ? compressed
    : new File([compressed], renameToJpeg(originalName), { type: 'image/jpeg' })

  return {
    file: finalFile,
    originalName,
    originalSize,
    finalSize: finalFile.size,
    wasConverted
  }
}
